/**
 * Route to Navigation Converter
 *
 * Transforms RouteExtension objects from the route registry into NavItemConfig objects
 * for use in NavigationSide component. Handles hierarchy, permissions, and icon resolution.
 */
import React from 'react';
import { RouteExtension } from '../types/route-config';
import { resolveIconComponent } from './icon-resolver';
import { getRouteRegistry } from '../registration/route-registry';

/**
 * Plugin metadata for navigation grouping
 */
export interface PluginNavMetadata {
  /** Display name for the plugin group (shown in navigation) */
  displayName: string;
  /** Order of the plugin group in navigation (lower = higher) */
  order: number;
  /** Lucide icon name shown beside the group header (e.g. 'ShoppingCart') */
  icon?: string;
}

/**
 * Navigation item configuration (matches design system)
 */
export interface NavItemConfig {
  id: string;
  label: string;
  path: string;
  icon: React.ReactElement<{ color?: string; size?: number }>;
  onClick?: () => void;
  children?: NavItemConfig[];
  depth?: number;
  /** Plugin ID this nav item belongs to */
  pluginId?: string;
  /** Whether this is a group separator */
  isSeparator?: boolean;
  /** Whether this is a group header (section title) */
  isGroupHeader?: boolean;
}

/**
 * Options for building navigation from routes
 */
export interface BuildNavOptions {
  /** Routes to process (defaults to root routes from registry) */
  routes?: RouteExtension[];

  /** Current path for active state determination */
  currentPath?: string;

  /** User permissions for filtering */
  userPermissions?: string[];

  /** Audience of the current user: 'partner' (external) or 'internal'. */
  userAudience?: 'internal' | 'partner';

  /** Custom filter function */
  filterFn?: (route: RouteExtension) => boolean;

  /** Navigation click handler */
  onNavigate?: (path: string) => void;

  /** Current depth (for recursion, default 0) */
  depth?: number;

  /** Whether to group nav items by plugin with separators (default: false) */
  groupByPlugin?: boolean;

  /** Plugin metadata for grouping (pluginId -> metadata) */
  pluginMetadata?: Record<string, PluginNavMetadata>;
}

/**
 * Check if user has required permissions for a route
 */
function hasPermission(
  route: RouteExtension,
  userPermissions?: string[]
): boolean {
  if (!route.permissions || route.permissions.length === 0) {
    return true; // No permissions required
  }

  if (!userPermissions || userPermissions.length === 0) {
    return false; // Route requires permissions but user has none
  }

  // Check if user has any of the required permissions. A user permission grants
  // a required one if it is the global wildcard '*', an exact match, or a
  // segment-wildcard match (e.g. 'vendor:*' grants 'vendor:application:read') —
  // mirroring the backend RBAC wildcard semantics.
  return route.permissions.some((permission) => {
    const permissionPattern = typeof permission === 'string' ? permission : permission.pattern;
    return userPermissions.some((userPerm) => permissionMatches(userPerm, permissionPattern));
  });
}

/**
 * Whether a route's nav item should be shown to the current user's audience.
 * Routes default to 'all'; 'partner'-only items are hidden from internal users
 * and 'internal'-only items from external partner users.
 */
function audienceMatches(
  route: RouteExtension,
  userAudience?: 'internal' | 'partner'
): boolean {
  const audience = route.metadata?.audience ?? 'all';
  if (audience === 'all' || !userAudience) return true;
  return audience === userAudience;
}

/**
 * Whether a user-held permission grants a required permission.
 *
 * Mirrors the backend PermissionResolver (and plugin-sdk `matchesPermission`)
 * semantics: `*` grants everything, exact match, and PREFIX wildcards
 * (`order:*` grants `order:sales:read`; `order:sales:*` grants `order:sales:read`).
 *
 * Kept in sync manually with `@lognai/plugin-sdk` `matchesPermission` — frontend-core
 * cannot depend on plugin-sdk. Using prefix semantics here (rather than the old
 * equal-segment-count rule) is what makes the sidebar agree with the route guard and
 * the backend: a domain-wildcard grant such as `order:*` (e.g. a FULL-access partner)
 * now correctly reveals `order:sales:read`-gated nav items instead of hiding them.
 */
function permissionMatches(userPerm: string, required: string): boolean {
  if (userPerm === '*' || userPerm === required) {
    return true;
  }
  // Prefix wildcard: 'order:*' -> prefix 'order:' must lead `required`.
  if (userPerm.endsWith(':*')) {
    const prefix = userPerm.slice(0, -1); // drop the '*', keep the trailing ':'
    return required.startsWith(prefix);
  }
  return false;
}

/**
 * Check if route should be shown in navigation
 */
function shouldShowInNav(route: RouteExtension): boolean {
  // Must have showInNav set to true
  if (!route.metadata?.showInNav) {
    return false;
  }

  // Must have a name
  if (!route.name) {
    return false;
  }

  // Skip routes with dynamic parameters (e.g., /:id/)
  // These are typically detail pages, not nav items
  if (route.path.includes(':')) {
    return false;
  }

  return true;
}

/**
 * Get sort order for a route
 * Uses metadata.order, falling back to route.order, falling back to 999
 */
function getSortOrder(route: RouteExtension): number {
  if (route.metadata?.order !== undefined) {
    return route.metadata.order;
  }
  if (route.order !== undefined) {
    return route.order;
  }
  return 999; // Default to end
}

/**
 * Convert a single RouteExtension to NavItemConfig
 */
function routeToNavItem(
  route: RouteExtension,
  options: BuildNavOptions
): NavItemConfig {
  const { onNavigate, depth = 0 } = options;
  const registry = getRouteRegistry();

  // Resolve icon
  const icon = resolveIconComponent(route.metadata?.icon, 20);

  // Get children for this route (don't group children by plugin)
  const childRoutes = registry.getChildRoutes(route.id);
  const children = childRoutes.length > 0
    ? buildNavigationFromRoutes({
        ...options,
        routes: childRoutes,
        depth: depth + 1,
        groupByPlugin: false,
      })
    : undefined;

  return {
    id: route.id,
    label: route.name || route.path,
    path: route.path,
    icon,
    onClick: () => {
      if (onNavigate) {
        onNavigate(route.path);
      }
    },
    children,
    depth,
    pluginId: route.pluginId,
  };
}

/**
 * Build navigation items from routes
 *
 * @param options - Build options
 * @returns Array of NavItemConfig for NavigationSide
 */
export function buildNavigationFromRoutes(
  options: BuildNavOptions = {}
): NavItemConfig[] {
  const {
    routes: inputRoutes,
    userPermissions,
    userAudience,
    filterFn,
    depth = 0,
    groupByPlugin = false,
    pluginMetadata = {},
  } = options;

  const registry = getRouteRegistry();

  // Get routes to process (defaults to root routes)
  const routes = inputRoutes || registry.getRootRoutes();

  // Filter routes
  const filteredRoutes = routes
    .filter(shouldShowInNav)
    .filter((route) => hasPermission(route, userPermissions))
    .filter((route) => audienceMatches(route, userAudience))
    .filter((route) => !filterFn || filterFn(route));

  // If grouping by plugin, organize routes by plugin and add separators
  if (groupByPlugin && depth === 0) {
    // Group routes by plugin
    const routesByPlugin = new Map<string, RouteExtension[]>();

    for (const route of filteredRoutes) {
      const pluginId = route.pluginId;
      if (!routesByPlugin.has(pluginId)) {
        routesByPlugin.set(pluginId, []);
      }
      routesByPlugin.get(pluginId)!.push(route);
    }

    // Sort routes within each plugin by order
    for (const routes of routesByPlugin.values()) {
      routes.sort((a, b) => getSortOrder(a) - getSortOrder(b));
    }

    // Sort plugins by their metadata order
    const sortedPluginIds = Array.from(routesByPlugin.keys()).sort((a, b) => {
      const orderA = pluginMetadata[a]?.order ?? 50;
      const orderB = pluginMetadata[b]?.order ?? 50;
      return orderA - orderB;
    });

    // Build nav items with group headers and separators between plugin groups
    const navItems: NavItemConfig[] = [];
    const totalGroups = sortedPluginIds.length;

    for (let i = 0; i < totalGroups; i++) {
      const pluginId = sortedPluginIds[i];
      const pluginRoutes = routesByPlugin.get(pluginId)!;
      const isLastGroup = i === totalGroups - 1;

      // 1. Add group header (section title)
      const displayName = pluginMetadata[pluginId]?.displayName || pluginId;
      const headerIconName = pluginMetadata[pluginId]?.icon;
      navItems.push({
        id: `header-${pluginId}`,
        label: displayName,
        path: '',
        icon: headerIconName
          ? resolveIconComponent({ name: headerIconName, size: 20 }, 20)
          : (null as any),
        isGroupHeader: true,
        pluginId,
      });

      // 2. Add nav items for this plugin
      for (const route of pluginRoutes) {
        navItems.push(routeToNavItem(route, { ...options, depth }));
      }

      // 3. Add separator after group (except for the last group)
      if (!isLastGroup) {
        navItems.push({
          id: `separator-${pluginId}`,
          label: '',
          path: '',
          icon: null as any,
          isSeparator: true,
          pluginId,
        });
      }
    }

    return navItems;
  }

  // Standard flow (no grouping): sort and convert
  const navItems = filteredRoutes
    .sort((a, b) => getSortOrder(a) - getSortOrder(b))
    .map((route) => routeToNavItem(route, { ...options, depth }));

  return navItems;
}

/**
 * Find the active route ID from current path
 *
 * @param path - Current path (e.g., "/warehouse/list")
 * @returns Route ID or undefined
 */
export function getActiveRouteId(path: string): string | undefined {
  const registry = getRouteRegistry();
  const route = registry.getRouteByPath(path);
  return route?.id;
}

/**
 * Check if a route or any of its descendants matches the given path
 *
 * @param route - Route to check
 * @param path - Path to match
 * @returns True if route or descendant matches
 */
export function isRouteOrDescendantActive(
  route: RouteExtension,
  path: string
): boolean {
  // Check if this route matches
  if (route.path === path) {
    return true;
  }

  // Check if path starts with route path (for nested routes)
  if (path.startsWith(route.path + '/')) {
    return true;
  }

  // Check children
  if (route.children && route.children.length > 0) {
    return route.children.some((child) =>
      isRouteOrDescendantActive(child, path)
    );
  }

  return false;
}
